import { EntityInfo } from '@components/atoms/EntityInfo/EntityInfo';
import { EntitySectionTitle } from '@components/atoms/EntitySectionTitle/EntitySectionTitle';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { ArticleTeaserCard } from '@components/organisms/ArticleTeaserCard/ArticleTeaserCard';
import { EntityMix, type EntityMixVariables } from '@components/organisms/EntityMix/EntityMix';
import { Persons, personenSsrLogic } from '@components/pages/personen';
import { Default } from '@components/templates/layouts/Default';
import {
  GetArticleByIdSimpleDocument,
  GetEntityMixDocument,
  GetPersonByIdDocument,
  GetPersonBySlugDocument,
} from '@lib/graphql/generated';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { prerenderPaths } from '@lib/graphql/helpers/prerenderPaths';
import { TvmEntityType } from '@lib/graphql/types';
import { createUrqlClient } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';
import { IconPersonPlaceholder } from '@src/assets/icon-person-placeholder';
import { GetArticleByIdDocument, GetBySlugDocument } from '@src/lib/graphql/generated';
import { decodeOptions } from '@src/utils/urlHelpers';
import { isUuid } from '@utils/common';
import { getCookie } from '@utils/cookies';
import { calculateAge, formatDate } from '@utils/dateHelpers';
import { getGenderLabel } from '@utils/personGender';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
  Image,
} from '@vgn-medien-holding/vgn-fe-components';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ArticleBody } from '../[categoryOrPage]/[slug]';

const entityTypeFilter = {
  movies: TvmEntityType.Movie,
  shows: TvmEntityType.Tvshow,
};

// Shared by the EntityMix query below and by the SSR prefetch in getStaticProps, so
// both produce the same urql cache key and the first page hydrates from urqlState
// instead of refetching on mount.
const buildEntityVariables = ({ personId = null, entityType = null } = {}): EntityMixVariables => ({
  first: 20,
  page: 1,
  person: personId || undefined,
  type: entityTypeFilter[entityType],
});

function Person({ data, slug }) {
  return slug === 'index' ? <Persons /> : <PersonSlug data={data} />;
}

// Unterseite Stars
const ad_settings_UnterseiteStars = {
  billboard: {
    id: 5256692,
    name: 'TVM_Unterseite_Stars_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256641,
    name: 'TVM_Mobile_Unterseite_Stars_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256615,
    name: 'TVM_Mobile_Unterseite_Stars_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function PersonSlug({ data }) {
  const { person, article, oewa, articles = [] } = data;
  const [filters, setFilters] = useState({
    entityType: null,
    provider: null,
  });

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Person',
    titel: person.name,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'RedCont',
    oewaSubkontingent: oewa?.category_key || 'KulturUndFreizeit',
    oewaProfilingkontingent: oewa?.profile_key || 'KulturUeberblick',
    cookieAlert: getCookie('OptanonConsent'),
  });

  const appearanceCount = (person?.movies?.paginatorInfo?.total || 0) + (person?.shows?.paginatorInfo?.total || 0);
  const genderLabel = getGenderLabel(person?.gender);

  return (
    <Default>
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteStars }} />

      <aside>
        <AditionTagPlacement
          tag={ad_settings_UnterseiteStars.mobile_top}
          classProps={{ root: 'mb-4 grid place-items-center lg:hidden' }}
        />
      </aside>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'KulturUeberblick'}
      />
      <Head>{Meta(person.metadata, `${person.metadata?.meta_title} | TV-MEDIA`)}</Head>
      <main className="dark grid prose-a:text-white text-white">
        <div className="row-start-2 mt-8 grid flex-wrap gap-4 text-center lg:row-start-auto lg:mt-0 lg:flex lg:items-center lg:justify-center">
          <select
            className="input-tvm-classic min-w-80! lg:w-auto lg:px-8 lg:py-3 lg:pr-16 lg:text-center lg:font-semibold lg:text-2xl"
            value={filters.entityType || ''}
            onChange={(item) => {
              setFilters((prev) => ({
                ...prev,
                entityType: item?.target.value,
              }));
            }}
          >
            <option value="all">Alle Inhalte</option>
            <option value="movies">Filme</option>
            <option value="shows">Serien</option>
          </select>
        </div>
        <article className="row-start-1 mt-12 px-4 sm:px-8 md:px-16 lg:row-start-auto">
          <section
            className={twMerge(
              'mx-auto grid gap-10 border-gray-650/25 border-y bg-gray-820/70 px-4 py-8 text-center sm:px-12 lg:grid-cols-[auto_1fr] lg:justify-start lg:text-left',
              person.died_at ? 'max-w-310' : 'max-w-272.5',
            )}
          >
            <div className="relative mx-auto size-52 overflow-hidden rounded-full">
              {person?.image?.url ? (
                <Image
                  priority
                  src={person.image.url}
                  alt={person.name}
                  fill
                  sizes="400px"
                  classProps={{ root: 'object-cover' }}
                  copyright={person?.image?.copyright_text}
                />
              ) : (
                <div className="flex size-full items-end justify-center bg-gray-600 text-gray-500">
                  <IconPersonPlaceholder classProps={{ root: 'size-full' }} />
                </div>
              )}
            </div>
            <div>
              <Title level={1} classProps={{ heading: 'text-3xl lg:text-4xl' }}>
                {person.name}
              </Title>
              <div
                dangerouslySetInnerHTML={{ __html: person.summary }}
                className="mt-3 line-clamp-5 lg:line-clamp-2"
              ></div>
              <div className="mt-8 hidden grid-cols-2 gap-y-1 sm:grid lg:flex lg:[&>div:not(:first-child)]:border-gray-650/65 lg:[&>div:not(:first-child)]:border-l [&>div:nth-child(even)]:border-gray-650/65 [&>div:nth-child(even)]:border-l">
                {appearanceCount > 0 && (
                  <EntityInfo
                    classProps={{
                      value: 'text-base',
                      label: 'text-gray-400',
                      root: 'divider-item',
                    }}
                    value={appearanceCount}
                    label="Auftritte"
                  />
                )}
                {genderLabel && (
                  <EntityInfo
                    classProps={{
                      value: 'text-base',
                      label: 'text-gray-400',
                      root: 'divider-item',
                    }}
                    value={genderLabel}
                    label="Geschlecht"
                  />
                )}
                <EntityInfo
                  classProps={{
                    value: 'text-base',
                    label: 'text-gray-400',
                    root: 'divider-item',
                  }}
                  value={person.born_at ? formatDate(person.born_at) : 'k.A.'}
                  label="Geboren am"
                />
                {person.died_at && (
                  <EntityInfo
                    classProps={{
                      value: 'text-base',
                      label: 'text-gray-400',
                      root: 'divider-item',
                    }}
                    value={formatDate(person.died_at)}
                    label="Verstorben am"
                  />
                )}
                <EntityInfo
                  classProps={{
                    value: 'text-base',
                    label: 'text-gray-400',
                    root: 'divider-item',
                  }}
                  value={person.born_at ? calculateAge(person.born_at, person.died_at) : 'k.A.'}
                  label="Alter"
                />
              </div>
            </div>
          </section>

          <aside>
            <AditionTagWithFallback
              tag={ad_settings_UnterseiteStars.billboard}
              fallback={ad_settings_UnterseiteStars.mobile_middle1}
              breakpoint={'lg'}
              classProps={{ root: 'my-5 grid place-items-center' }}
            />
          </aside>
        </article>
        <EntityMix
          classProps={{ root: 'mt-16' }}
          variables={buildEntityVariables({ personId: person.id, entityType: filters?.entityType })}
          skip={!person?.id}
        />
        {article && (
          <article className="mx-auto mt-32 max-w-272.5 px-4 md:px-8">
            <Title
              classProps={{
                heading: 'px-8 lg:px-32 text-3xl leading-11 lg:text-4xl lg:leading-12',
              }}
              level={1}
            >
              {article.headline}
            </Title>
            <div>
              <ArticleBody content={article.content} />
            </div>
          </article>
        )}
        {articles?.length > 0 && (
          <section className="mt-12 min-w-0 border-gray-650 border-t pt-12">
            <EntitySectionTitle>Verwandte Artikel</EntitySectionTitle>
            <div className="tvm-scrollbar min-w-0 overflow-x-auto overflow-y-hidden pb-8">
              <div className="grid h-full grid-flow-col gap-3">
                {articles.map((article, index) => (
                  <ArticleTeaserCard key={index} article={article} layout="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </Default>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: await prerenderPaths(['/personen/index']),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  // Person, redirect and article lookups reach the page through props, never
  // through a client query, so they run on a throwaway client whose cache is not
  // shipped. Only the EntityMix query below stays in the SSR payload.
  const { ssrQuery: ssrPropsQuery } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  if (options?.slug === 'index') {
    return await personenSsrLogic();
  }

  const { data: personData } = isUuid(options.slug)
    ? await ssrPropsQuery({
        query: GetPersonByIdDocument,
        variables: { id: options.slug },
      })
    : await ssrPropsQuery({
        query: GetPersonBySlugDocument,
        variables: { slug: options.slug },
      });
  const person = personData?.person;

  // Handle redirect result
  if (person?.metadata?.redirect) {
    return handleRedirect(301, person?.metadata?.redirect);
  }

  // Redirect asset-ID (UUID) URLs to the canonical slug URL to avoid duplicate content
  if (isUuid(options.slug) && person?.slug && person.slug !== options.slug) {
    return handleRedirect(301, `/personen/${person.slug}`);
  }

  if (!person) {
    // Check if a redirect is in place
    const { data: pageData } = await ssrPropsQuery({
      query: GetBySlugDocument,
      variables: {
        portal: 'tvmedia',
        slug: `/personen/${options.slug}`,
      },
    });

    // Handle redirect result
    if (pageData?.findBySlug?.__typename === 'Redirect' && pageData?.findBySlug?.redirect_type) {
      return handleRedirect(
        pageData?.findBySlug?.redirect_type,
        pageData?.findBySlug?.redirect_target != null ? pageData?.findBySlug?.redirect_target : '',
      );
    }
  }

  if (!person) return { notFound: true };

  const data = { person, article: null, articles: [] };

  if (person.article_id) {
    const { data: articleData } = await ssrPropsQuery({
      query: GetArticleByIdDocument,
      variables: { id: person.article_id },
    });
    data.article = articleData?.article;
  }

  if (person.article_ids?.length > 0) {
    const articleResults = await Promise.all(
      person.article_ids.map((id) => ssrPropsQuery({ query: GetArticleByIdSimpleDocument, variables: { id } })),
    );
    data.articles = articleResults.map((r) => r.data?.article).filter(Boolean);
  }

  // Prefetch the first entity page so the statically generated markup (cached by
  // Varnish) already contains the teasers, instead of a client round trip.
  try {
    await ssrQuery({
      query: GetEntityMixDocument,
      variables: buildEntityVariables({ personId: person.id }),
    });
  } catch {
    // Prefetch is an optimisation only - the client refetches on mount.
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      urqlState: extractSsrState(ssrCache),
    },
  };
};

export default Person;
